import { Component, Mixins } from "vue-property-decorator";
import AxiosMixin from "@/mixins/axiosMixin";
import FireStoreMixin, { Collections } from "@/mixins/firestoreMixin";
import UtilMixin from "@/mixins/utilMixin";
import dayjs from "dayjs";
import { R8_CHANGE_LAW_BASE_YM } from "@/const/common";

/**
 * アラートA: 記録Ⅱ操作前に同一建物減算の事前チェック実施状況を確認し、
 * 事業所として実施済みの月であれば注意喚起ダイアログを表示する。
 * 判定は事業所単位（pre_check_office_exec_dates.pre_check_date_same_building）。
 * 結果は yearmonth+officeId をキーにキャッシュし、同一画面内で複数回呼ばれても
 * API は1回のみ叩く（C3: パフォーマンス制約）。
 */
@Component
export default class PrecheckAlertMixin extends Mixins(
  AxiosMixin,
  FireStoreMixin,
  UtilMixin
) {
  private precheckSameBuildingCache: Record<string, boolean> = {};

  /**
   * 記録Ⅱの状態変更（提出・差戻し・削除）前に呼ぶ。
   * @param officeId  利用者の所属事業所ID
   * @param visitDatetime  訪問日時文字列（"YYYY-MM-DD ..." 形式）
   * @param isMedical  医療保険の訪問かどうか（false なら同一建物減算に影響しないためスキップ）
   * @returns true=続行 / false=ユーザーがキャンセル
   */
  protected async checkPrecheckAlertForRecord2(
    officeId: number,
    visitDatetime: string,
    isMedical: boolean
  ): Promise<boolean> {
    if (!visitDatetime || !officeId) return true;
    if (!isMedical) return true;

    const yearmonth = visitDatetime.substring(0, 7);
    if (yearmonth < R8_CHANGE_LAW_BASE_YM) return true;

    const isDone = await this._fetchPrecheckSameBuildingDone(
      officeId,
      yearmonth
    );
    if (!isDone) return true;

    return this.$openConfirm(
      "レセプト事前チェックが処理されています。\n事業所全体の正しい算定に影響する場合があるため、再度レセプト事前チェックを処理する必要があります。\nよろしいですか？"
    );
  }

  private _fetchPrecheckSameBuildingDone(
    officeId: number,
    yearmonth: string
  ): Promise<boolean> {
    const key = `${yearmonth}-${officeId}`;
    if (key in this.precheckSameBuildingCache) {
      return Promise.resolve(this.precheckSameBuildingCache[key]);
    }
    return new Promise(resolve => {
      this.postJsonBackground(
        window.base_url + "/api/receipt/preCheck/officeExecDate/get",
        {
          yearmonth: parseInt(yearmonth.replace("-", "")),
          office_id: officeId
        },
        res => {
          const isDone = !!res.data.data?.pre_check_date_same_building;
          this.precheckSameBuildingCache[key] = isDone;
          resolve(isDone);
        },
        () => {
          resolve(false);
        }
      );
    });
  }

  private get _precheckAlertSuppressKey(): string {
    // iBow連携済みユーザーの場合はewellUserIdで、未連携のユーザーはログインIDを元に「本日は表示しない」フラグを管理
    return this.loginUser.ewell_user_id
      ? `ewell_${this.loginUser.ewell_user_id}`
      : `staff_${this.loginUser.id}`;
  }

  /** 当日の非表示設定が Firestore に保存済みかを確認 */
  protected async isPrecheckAlertSuppressedToday(): Promise<boolean> {
    const { data, error } = await this.getFirestore(
      Collections.PrecheckAlertSuppress,
      this._precheckAlertSuppressKey
    );
    if (error) throw error;
    const today = dayjs().format("YYYY-MM-DD");
    return !!data && data.date === today;
  }

  /** 当日の非表示設定を Firestore に保存 */
  protected async savePrecheckAlertSuppress(): Promise<void> {
    await this.setFirestore(
      Collections.PrecheckAlertSuppress,
      this._precheckAlertSuppressKey,
      { date: dayjs().format("YYYY-MM-DD") }
    );
  }

  /**
   * 記録Ⅲ存在チェック（Record2.vue 用）
   * 包括型ONかつ該当月の記録Ⅲが存在する場合、確認ダイアログを表示する。
   * Record3CheckMixin を継承せず直接実装することで、Record2.vue での
   * ミックスイン数を増やさずに両チェックを提供する。
   * @returns true=続行 / false=中止
   */
  protected checkAndConfirmRecord3Delete(
    patientId: number,
    visitDatetime: string
  ): Promise<boolean> {
    return new Promise(resolve => {
      if (!visitDatetime) {
        resolve(true);
        return;
      }
      const yearmonth = parseInt(
        visitDatetime.substring(0, 7).replace("-", "")
      );
      this.postJsonBackground(
        window.base_url + "/api/patient/visitrecord3s/existsForMonth",
        { patient_id: patientId, yearmonth: yearmonth },
        res => {
          if (res.data.medical_comprehensive_flg !== 1) {
            resolve(true);
            return;
          }
          if (!res.data.exists) {
            resolve(true);
            return;
          }
          this.$openConfirm(
            "レセプト事前チェックが処理されています。事業所全体の正しい算定に影響する場合があるため、再度レセプト事前チェックを処理する必要があります。よろしいですか？"
          ).then((confirmed: boolean) => {
            resolve(confirmed);
          });
        },
        () => {
          resolve(true);
        }
      );
    });
  }
}
